import {useState, useRef, useCallback, useEffect} from 'react';
import {getRepository} from '../data/repository';
import type {StagedDocument, Profile, FinancialYear, DocumentType, DuplicateCheckResult} from '../types/vault';
import {config} from '../data/config';
import {extractPastedImageFiles} from '../utils/clipboard';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const {
  FiUpload, FiX, FiCamera, FiFolder, FiTrash2, FiRefreshCw,
  FiCheck, FiAlertCircle, FiSkipForward, FiFilePlus, FiGitBranch, FiExternalLink
} = FiIcons;

type DuplicateAction = 'pending' | 'skip' | 'new' | 'version';

interface DocumentUploadProps {
  isOpen: boolean;
  onClose: () => void;
  profiles: Profile[];
  financialYears: FinancialYear[];
  onUploadComplete: () => void;
  initialProfileId?: string;
  initialFinancialYearId?: string;
  initialTitle?: string;
  initialDocumentType?: DocumentType;
  initialTags?: string;
  lockFinancialYearContext?: boolean;
}

function validateFile(file: File): {valid: boolean; error?: string} {
  if (file.size === 0) return {valid: false, error: 'This file is empty and cannot be uploaded.'};
  if (file.size > config.upload.maxFileSize) return {valid: false, error: 'File exceeds the 5 MB limit.'};
  const ext = '.' + (file.name.split('.').pop()?.toLowerCase() || '');
  if (!config.upload.allowedExtensions.includes(ext)) return {valid: false, error: 'Unsupported file type. Upload PDF, image, CSV, Excel or Word files.'};
  return {valid: true};
}

function normalizeMimeType(file: File): string {
  if (file.type && file.type !== 'application/octet-stream' && file.type !== 'binary/octet-stream') return file.type;
  const ext = file.name.split('.').pop()?.toLowerCase() || '';
  return config.upload.mimeMap[ext] || 'application/octet-stream';
}

async function computeChecksumLocal(file: File): Promise<string> {
  const buffer = await file.arrayBuffer();
  const hashBuffer = await crypto.subtle.digest('SHA-256', buffer);
  return Array.from(new Uint8Array(hashBuffer)).map(b => b.toString(16).padStart(2, '0')).join('');
}

function isEditableTarget(el: Element | null): boolean {
  if (!el) return false;
  const tag = el.tagName.toLowerCase();
  if (tag === 'input' || tag === 'textarea' || tag === 'select') return true;
  if ((el as HTMLElement).isContentEditable) return true;
  if (el.closest('[contenteditable="true"]')) return true;
  return false;
}

export function DocumentUpload({
  isOpen, onClose, profiles, financialYears, onUploadComplete,
  initialProfileId, initialFinancialYearId, initialTitle, initialDocumentType, initialTags,
  lockFinancialYearContext
}: DocumentUploadProps) {
  const [queue, setQueue] = useState<StagedDocument[]>([]);
  const [duplicates, setDuplicates] = useState<Record<string, {result: DuplicateCheckResult; action: DuplicateAction; error?: string}>>({});
  const [sharedTitle, setSharedTitle] = useState('');
  const [sharedProfileId, setSharedProfileId] = useState('');
  const [sharedFinancialYearId, setSharedFinancialYearId] = useState('');
  const [sharedDocumentType, setSharedDocumentType] = useState<DocumentType>('Receipt');
  const [sharedTags, setSharedTags] = useState('');
  const [sharedSensitive, setSharedSensitive] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isStaging, setIsStaging] = useState(false);
  const [stagingError, setStagingError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [rejectedFiles, setRejectedFiles] = useState<{name: string; reason: string}[]>([]);
  const [pasteConfirm, setPasteConfirm] = useState<string | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const dragDepthRef = useRef(0);
  const queueRef = useRef<StagedDocument[]>([]);
  const pasteTimerRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => { queueRef.current = queue; }, [queue]);

  useEffect(() => {
    if (isOpen) {
      if (initialProfileId) setSharedProfileId(initialProfileId);
      if (initialFinancialYearId) setSharedFinancialYearId(initialFinancialYearId);
      if (initialTitle) setSharedTitle(initialTitle);
      if (initialDocumentType) setSharedDocumentType(initialDocumentType);
      if (initialTags) setSharedTags(initialTags);
    }
  }, [isOpen, initialProfileId, initialFinancialYearId, initialTitle, initialDocumentType, initialTags]);

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const addFiles = useCallback(async (files: File[]) => {
    setIsStaging(true);
    setStagingError(null);
    try {
      const newRejected: {name: string; reason: string}[] = [];
      const validFiles: File[] = [];
      for (const file of files) {
        const {valid, error} = validateFile(file);
        if (!valid) {
          newRejected.push({name: file.name, reason: error!});
        } else {
          const dup = queueRef.current.some(q =>
            q.file.name === file.name && q.file.size === file.size && q.file.lastModified === file.lastModified
          );
          if (!dup) validFiles.push(file);
        }
      }
      setRejectedFiles(prev => [...prev, ...newRejected]);
      if (validFiles.length > 0) {
        const staged = await getRepository().stageDocuments(validFiles);
        const newItems = staged.map((item, idx) => {
          let tTitle = sharedTitle || item.title;
          if (sharedTitle && validFiles.length > 1) {
            tTitle = `${sharedTitle} ${String(queueRef.current.length + idx + 1).padStart(2, '0')}`;
          }
          return {
            ...item,
            title: tTitle,
            profileId: sharedProfileId,
            financialYearId: sharedFinancialYearId || undefined,
            documentType: sharedDocumentType,
            tags: sharedTags ? sharedTags.split(',').map(t => t.trim()).filter(Boolean) : [],
            sensitive: sharedSensitive,
            status: 'staged' as const
          };
        });
        setQueue(prev => [...prev, ...newItems]);
        for (const item of newItems) {
          computeChecksumAsync(item.id, item.file);
          if (sharedProfileId) checkDuplicateAsync(item);
        }
      }
    } catch (err) {
      setStagingError(err instanceof Error ? err.message : 'An error occurred while preparing files.');
    } finally {
      setIsStaging(false);
    }
  }, [sharedProfileId, sharedFinancialYearId, sharedDocumentType, sharedTags, sharedSensitive, sharedTitle]);

  const computeChecksumAsync = async (itemId: string, file: File) => {
    try {
      const checksum = await computeChecksumLocal(file);
      setQueue(prev => prev.map(item => item.id === itemId ? {...item, checksum, status: 'ready' as const} : item));
    } catch {
      setQueue(prev => prev.map(item => item.id === itemId ? {...item, error: 'Checksum calculation failed. You can still upload.', status: 'staged' as const} : item));
    }
  };

  const checkDuplicateAsync = async (item: StagedDocument) => {
    if (!item.profileId) return;
    try {
      const result = await getRepository().checkDocumentDuplicates(item);
      setDuplicates(prev => ({...prev, [item.id]: {result, action: result.isDuplicate ? 'pending' : 'skip'}}));
    } catch {
      setDuplicates(prev => ({...prev, [item.id]: {result: {isDuplicate: false}, action: 'pending', error: 'Duplicate check failed. Retry to resolve.'}}));
    }
  };

  const retryDuplicateCheck = (itemId: string) => {
    const item = queueRef.current.find(q => q.id === itemId);
    if (item && item.profileId) checkDuplicateAsync(item);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files) { addFiles(Array.from(files)); e.target.value = ''; }
  };

  const handleCameraSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files) { addFiles(Array.from(files)); e.target.value = ''; }
  };

  const handleDragEnter = (e: React.DragEvent) => { e.preventDefault(); e.stopPropagation(); dragDepthRef.current += 1; setIsDragging(true); };
  const handleDragOver = (e: React.DragEvent) => { e.preventDefault(); e.stopPropagation(); };
  const handleDragLeave = (e: React.DragEvent) => { e.preventDefault(); e.stopPropagation(); dragDepthRef.current -= 1; if (dragDepthRef.current === 0) setIsDragging(false); };
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault(); e.stopPropagation(); dragDepthRef.current = 0; setIsDragging(false);
    if (e.dataTransfer.files?.length) addFiles(Array.from(e.dataTransfer.files));
  };

  // --- Paste handler ---
  useEffect(() => {
    if (!isOpen) return;

    const handlePaste = (e: ClipboardEvent) => {
      if (isEditableTarget(e.target as Element)) return;
      const imageFiles = extractPastedImageFiles(e.clipboardData?.items ?? null);
      if (imageFiles.length === 0) return;
      e.preventDefault();
      addFiles(imageFiles);
      setPasteConfirm(`${imageFiles.length} pasted screenshot${imageFiles.length !== 1 ? 's' : ''} added`);
      clearTimeout(pasteTimerRef.current);
      pasteTimerRef.current = setTimeout(() => setPasteConfirm(null), 3000);
    };

    document.addEventListener('paste', handlePaste);
    return () => {
      document.removeEventListener('paste', handlePaste);
      clearTimeout(pasteTimerRef.current);
    };
  }, [isOpen, addFiles]);

  const updateItem = (id: string, updates: Partial<StagedDocument>) => {
    if (lockFinancialYearContext) { delete updates.profileId; delete updates.financialYearId; }
    setQueue(prev => prev.map(item => item.id === id ? {...item, ...updates} : item));
  };

  const removeItem = (id: string) => {
    setQueue(prev => prev.filter(item => item.id !== id));
    setDuplicates(prev => { const nd = {...prev}; delete nd[id]; return nd; });
  };

  const setDuplicateAction = (id: string, action: DuplicateAction) => {
    const dup = duplicates[id];
    if (!dup) return;
    setDuplicates(prev => ({...prev, [id]: {...prev[id], action}}));
    if (action === 'skip') updateItem(id, {status: 'skipped'});
    else if (action === 'version' && dup.result.existingDocumentId) updateItem(id, {duplicateAction: 'version', duplicateDocumentId: dup.result.existingDocumentId});
    else if (action === 'new') updateItem(id, {duplicateAction: 'new'});
  };

  const validateItem = (item: StagedDocument): string | null => {
    if (!item.title.trim()) return 'Title is required';
    if (item.title.length > config.limits.documentTitle) return `Title max ${config.limits.documentTitle} chars`;
    if (!item.profileId) return 'Profile is required';
    if (!item.documentType) return 'Document type is required';
    if (item.financialYearId) {
      const year = financialYears.find(fy => fy.id === item.financialYearId);
      if (!year || year.profileId !== item.profileId) return 'Financial year does not match profile';
    }
    if (item.notes && item.notes.length > config.limits.documentNotes) return `Notes max ${config.limits.documentNotes} chars`;
    if (item.tags?.some(t => t.length > config.limits.documentTag)) return `Each tag max ${config.limits.documentTag} chars`;
    return null;
  };

  const applySharedToAll = () => {
    const eligible = queue.filter(i => i.status !== 'uploaded' && i.status !== 'skipped');
    const trimmedTitle = sharedTitle.trim();
    setQueue(prev => prev.map(item => {
      if (item.status === 'uploaded' || item.status === 'skipped') return item;
      const updates: Partial<StagedDocument> = {};
      if (trimmedTitle) {
        if (eligible.length === 1) {
          updates.title = trimmedTitle;
        } else {
          const idx = eligible.findIndex(e => e.id === item.id);
          if (idx >= 0) updates.title = `${trimmedTitle} ${String(idx + 1).padStart(2, '0')}`;
        }
      }
      if (!lockFinancialYearContext) {
        if (sharedProfileId) updates.profileId = sharedProfileId;
        if (sharedFinancialYearId) updates.financialYearId = sharedFinancialYearId || undefined;
      }
      updates.documentType = sharedDocumentType;
      if (sharedTags) updates.tags = sharedTags.split(',').map(t => t.trim()).filter(Boolean);
      updates.sensitive = sharedSensitive;
      return {...item, ...updates};
    }));
    for (const item of eligible) {
      const effectiveProfileId = lockFinancialYearContext ? item.profileId : (sharedProfileId || item.profileId);
      if (effectiveProfileId) {
        const dupItem = {
          ...item,
          profileId: effectiveProfileId,
          financialYearId: lockFinancialYearContext ? item.financialYearId : (sharedFinancialYearId || item.financialYearId),
          title: trimmedTitle || item.title
        };
        checkDuplicateAsync(dupItem);
      }
    }
  };

  const handleUpload = async () => {
    setIsUploading(true);
    setStagingError(null);
    try {
      const repo = getRepository();
      let readyItems = queue.filter(item => {
        if (item.status === 'uploaded' || item.status === 'skipped') return false;
        const error = validateItem(item);
        if (error) { updateItem(item.id, {status: 'failed', error}); return false; }
        return true;
      });

      const itemsToCheck = readyItems.filter(item => item.profileId);
      if (itemsToCheck.length > 0) {
        const updatedDuplicates = {...duplicates};
        for (const item of itemsToCheck) {
          try {
            const dupResult = await repo.checkDocumentDuplicates(item);
            if (dupResult.isDuplicate) {
              const existingAction = updatedDuplicates[item.id]?.action;
              const resolvedAction = existingAction === 'new' || existingAction === 'version' ? existingAction : 'pending';
              updatedDuplicates[item.id] = {result: dupResult, action: resolvedAction};
            } else {
              updatedDuplicates[item.id] = {result: dupResult, action: 'skip'};
            }
          } catch {
            updatedDuplicates[item.id] = {result: {isDuplicate: false}, action: 'pending', error: 'Duplicate check failed. Retry to resolve.'};
          }
        }
        setDuplicates(updatedDuplicates);
        readyItems = readyItems.filter(item => {
          const dup = updatedDuplicates[item.id];
          if (dup?.action === 'pending') return false;
          return true;
        });
      }

      if (readyItems.length === 0) return;
      const results = await repo.uploadDocuments(readyItems);
      setQueue(prev => prev.map(item => {
        const result = results.find(r => r.id === item.id);
        return result || item;
      }));
      const hasFailures = results.some(i => i.status === 'failed');
      onUploadComplete();
      if (!hasFailures) setTimeout(handleClose, 1500);
    } catch (err) {
      setStagingError(err instanceof Error ? err.message : 'An unexpected error occurred during upload.');
    } finally {
      setIsUploading(false);
    }
  };

  const retryFailed = () => {
    setQueue(prev => prev.map(item => item.status === 'failed' ? {...item, status: 'staged' as const, error: undefined} : item));
  };

  const handleClose = () => {
    const failedCount = queue.filter(i => i.status === 'failed').length;
    if (isUploading || failedCount > 0) return;
    setQueue([]);
    setDuplicates({});
    setRejectedFiles([]);
    setStagingError(null);
    setPasteConfirm(null);
    onClose();
  };

  if (!isOpen) return null;

  const pendingCount = queue.filter(i => i.status !== 'uploaded' && i.status !== 'skipped' && i.status !== 'failed').length;
  const uploadedCount = queue.filter(i => i.status === 'uploaded').length;
  const failedCount = queue.filter(i => i.status === 'failed').length;
  const unresolvedDupCount = queue.filter(i => {
    const dup = duplicates[i.id];
    return dup?.action === 'pending' && i.status !== 'uploaded' && i.status !== 'skipped';
  }).length;

  const filteredSharedYears = sharedProfileId ? financialYears.filter(fy => fy.profileId === sharedProfileId) : financialYears;

  const statusLabel = (s: StagedDocument['status']) => {
    switch (s) {
      case 'staged': return 'Preparing';
      case 'ready': return 'Ready';
      case 'uploading': return 'Uploading';
      case 'uploaded': return 'Uploaded';
      case 'failed': return 'Failed';
      case 'skipped': return 'Skipped';
    }
  };

  const statusIcon = (s: StagedDocument['status']) => {
    switch (s) {
      case 'uploaded': return FiCheck;
      case 'failed': return FiAlertCircle;
      case 'skipped': return FiSkipForward;
      case 'uploading': return FiRefreshCw;
      default: return null;
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end lg:items-center justify-center">
      <div className="fixed inset-0 bg-black/40" onClick={handleClose} />
      <div
        className="relative w-full lg:max-w-4xl xl:max-w-5xl lg:rounded-2xl rounded-t-2xl h-dvh lg:h-auto lg:max-h-[90vh] flex flex-col shadow-xl"
        style={{backgroundColor: 'var(--mfv-card-surface)'}}
        role="dialog"
        aria-label="Upload documents"
      >
        <div className="flex items-center justify-between px-4 lg:px-6 py-4 border-b shrink-0 bg-white sticky top-0 z-10" style={{borderColor: 'var(--mfv-border)'}}>
          <div>
            <h2 className="text-lg font-semibold" style={{color: 'var(--mfv-text-main)'}}>Upload Documents</h2>
            <p className="text-xs mt-0.5" style={{color: 'var(--mfv-text-muted)'}}>
              {queue.length} file{queue.length !== 1 ? 's' : ''} • {uploadedCount} uploaded
              {failedCount > 0 && <span className="text-red-600 ml-2">{failedCount} failed</span>}
              {unresolvedDupCount > 0 && <span className="text-amber-600 ml-2">{unresolvedDupCount} blocked — resolve or retry</span>}
            </p>
          </div>
          <button onClick={handleClose} disabled={isUploading || failedCount > 0} aria-label="Close upload dialog" className="p-2 rounded-lg focus:outline-none focus:ring-2 disabled:opacity-50" style={{color: 'var(--mfv-text-muted)'}}>
            <SafeIcon icon={FiX} className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-4 lg:px-6 py-4 space-y-4">
          {lockFinancialYearContext && sharedProfileId && (
            <div className="rounded-xl p-3 border bg-blue-50 border-blue-200">
              <p className="text-sm text-blue-800 flex items-center">
                <SafeIcon icon={FiCheck} className="w-4 h-4 mr-2" />
                Attached to {profiles.find(p => p.id === sharedProfileId)?.displayName} • FY{financialYears.find(y => y.id === sharedFinancialYearId)?.endingYear}
              </p>
            </div>
          )}

          {stagingError && (<div className="rounded-xl p-3 border bg-red-50 border-red-200"><p className="text-sm text-red-700">{stagingError}</p></div>)}

          {pasteConfirm && (
            <div className="rounded-xl p-3 border bg-teal-50 border-teal-200 flex items-center">
              <SafeIcon icon={FiCheck} className="w-4 h-4 text-teal-600 mr-2 shrink-0" />
              <p className="text-sm text-teal-800">{pasteConfirm}</p>
            </div>
          )}

          {rejectedFiles.length > 0 && (
            <div className="rounded-xl p-3 border bg-red-50 border-red-200 space-y-1">
              <div className="flex items-center justify-between"><h3 className="text-sm font-medium text-red-800">Rejected Files</h3><button onClick={() => setRejectedFiles([])} className="text-xs text-red-600 hover:text-red-800">Clear</button></div>
              {rejectedFiles.map((r, i) => <p key={i} className="text-xs text-red-700"><strong>{r.name}</strong>: {r.reason}</p>)}
            </div>
          )}

          {isStaging && <div className="text-center py-2"><span className="inline-flex items-center px-3 py-1.5 rounded-lg text-sm font-medium bg-blue-50 text-blue-700"><SafeIcon icon={FiRefreshCw} className="w-3 h-3 mr-2 animate-spin" />Preparing files…</span></div>}

          <div
            onDragEnter={handleDragEnter}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            tabIndex={0}
            role="region"
            aria-label="File drop zone. Drag and drop files, select files, or paste a screenshot with Ctrl+V or Command+V."
            className={`border-2 border-dashed rounded-xl p-6 lg:p-8 text-center transition-colors focus:outline-none focus:ring-2 focus:ring-[#0F766E] ${isDragging ? 'border-[#0F766E] bg-[#0F766E]/5' : ''}`}
            style={{borderColor: isDragging ? '#0F766E' : 'var(--mfv-border)'}}
          >
            {isDragging ? (
              <div className="space-y-2"><SafeIcon icon={FiUpload} className="w-12 h-12 mx-auto text-[#0F766E]" /><p className="text-sm font-medium text-[#0F766E]">Drop files to add them</p></div>
            ) : (
              <div className="space-y-3">
                <SafeIcon icon={FiUpload} className="w-12 h-12 mx-auto" style={{color: 'var(--mfv-text-muted)'}} />
                <p className="text-sm" style={{color: 'var(--mfv-text-muted)'}}>Drag and drop files here, select files below, or paste a screenshot with <kbd className="px-1.5 py-0.5 bg-slate-100 border border-slate-200 rounded text-xs font-sans">Ctrl+V</kbd> / <kbd className="px-1.5 py-0.5 bg-slate-100 border border-slate-200 rounded text-xs font-sans">⌘V</kbd></p>
                <p className="text-xs" style={{color: 'var(--mfv-text-muted)'}}>Max {config.upload.maxFileSizeLabel} per file • PDF, images, CSV, Excel, Word</p>
                <a href="https://app.bpasolutions.au/SafePDF-local" target="_blank" rel="noopener noreferrer" aria-label="Open BPA Solutions file compressor in a new tab" className="mfv-utility-link">
                  <SafeIcon icon={FiExternalLink} className="w-3.5 h-3.5 mr-1.5" aria-hidden="true" />Compress Files Before Upload
                </a>
                <p className="text-xs" style={{color: 'var(--mfv-text-muted)'}}>PDF, image and ZIP compression opens in a new tab.</p>
                <div className="flex flex-wrap justify-center gap-3">
                  <button onClick={() => fileInputRef.current?.click()} className="mfv-btn-primary"><SafeIcon icon={FiFolder} className="w-4 h-4 mr-2" />Select Files</button>
                  <button onClick={() => cameraInputRef.current?.click()} className="mfv-btn-secondary lg:hidden"><SafeIcon icon={FiCamera} className="w-4 h-4 mr-2" />Camera</button>
                </div>
              </div>
            )}
            <input ref={fileInputRef} type="file" accept={config.upload.acceptAttribute} multiple onChange={handleFileSelect} className="hidden" />
            <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" onChange={handleCameraSelect} className="hidden" />
          </div>

          {queue.length > 0 && (
            <div className="rounded-xl p-4 border" style={{backgroundColor: 'var(--mfv-page-bg)', borderColor: 'var(--mfv-border)'}}>
              <h3 className="text-sm font-medium mb-1" style={{color: 'var(--mfv-text-main)'}}>Shared Details — Optional</h3>
              <p className="text-xs mb-3" style={{color: 'var(--mfv-text-muted)'}}>Set common details once, then apply them to every staged file. You can still edit each file individually below.</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
                <div>
                  <label className="block text-xs font-medium mb-1" style={{color: 'var(--mfv-text-muted)'}}>Shared Title</label>
                  <input type="text" value={sharedTitle} onChange={e => setSharedTitle(e.target.value)} placeholder="Optional common title" className="mfv-input" />
                  <p className="text-[10px] mt-1" style={{color: 'var(--mfv-text-muted)'}}>For multiple files, titles are numbered automatically.</p>
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1" style={{color: 'var(--mfv-text-muted)'}}>Profile</label>
                  <select disabled={lockFinancialYearContext} value={sharedProfileId} onChange={e => {setSharedProfileId(e.target.value); setSharedFinancialYearId('');}} className="mfv-input">
                    <option value="">Select Profile</option>{profiles.map(p => <option key={p.id} value={p.id}>{p.displayName}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1" style={{color: 'var(--mfv-text-muted)'}}>Financial Year</label>
                  <select disabled={lockFinancialYearContext} value={sharedFinancialYearId} onChange={e => setSharedFinancialYearId(e.target.value)} className="mfv-input">
                    <option value="">Not year-specific</option>{filteredSharedYears.map(fy => <option key={fy.id} value={fy.id}>FY{fy.endingYear}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1" style={{color: 'var(--mfv-text-muted)'}}>Document Type</label>
                  <select value={sharedDocumentType} onChange={e => setSharedDocumentType(e.target.value as DocumentType)} className="mfv-input">{config.documentCategories.map(t => <option key={t} value={t}>{t}</option>)}</select>
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-xs font-medium mb-1" style={{color: 'var(--mfv-text-muted)'}}>Tags</label>
                  <input type="text" value={sharedTags} onChange={e => setSharedTags(e.target.value)} placeholder="Tags (comma-separated)" className="mfv-input" />
                </div>
              </div>
              <div className="flex items-center justify-between">
                <label className="flex items-center text-sm" style={{color: 'var(--mfv-text-muted)'}}>
                  <label className="block text-xs font-medium mr-2" style={{color: 'var(--mfv-text-muted)'}}>Sensitive</label>
                  <input type="checkbox" checked={sharedSensitive} onChange={e => setSharedSensitive(e.target.checked)} className="mr-1 rounded" />
                </label>
                <button onClick={applySharedToAll} className="text-xs font-medium" style={{color: 'var(--mfv-teal)'}}>Apply to All Staged Files</button>
              </div>
            </div>
          )}

          {queue.length > 0 && (
            <div className="space-y-3">
              <div>
                <h3 className="text-sm font-medium" style={{color: 'var(--mfv-text-main)'}}>Staged Files ({queue.length})</h3>
                <p className="text-xs mt-0.5" style={{color: 'var(--mfv-text-muted)'}}>These are the files waiting to be uploaded. Review or override the title and details for each file before uploading.</p>
              </div>
              {queue.map(item => {
                const dup = duplicates[item.id];
                const error = validateItem(item);
                const itemYears = item.profileId ? financialYears.filter(fy => fy.profileId === item.profileId) : financialYears;
                const sIcon = statusIcon(item.status);
                return (
                  <div key={item.id} className="rounded-xl border p-4 space-y-3" style={{backgroundColor: 'var(--mfv-card-surface)', borderColor: 'var(--mfv-border)'}}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate" style={{color: 'var(--mfv-text-main)'}}>{item.file.name}</p>
                        <p className="text-xs mt-0.5" style={{color: 'var(--mfv-text-muted)'}}>{normalizeMimeType(item.file)} • {formatFileSize(item.file.size)}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${item.status === 'uploaded' ? 'bg-green-100 text-green-700' : item.status === 'failed' ? 'bg-red-100 text-red-700' : item.status === 'skipped' ? 'bg-slate-100 text-slate-600' : item.status === 'uploading' ? 'bg-blue-100 text-blue-700' : item.status === 'ready' ? 'bg-teal-100 text-teal-700' : 'bg-amber-100 text-amber-700'}`}>
                          {sIcon && <SafeIcon icon={sIcon} className={`w-3 h-3 mr-1 ${item.status === 'uploading' ? 'animate-spin' : ''}`} />}{statusLabel(item.status)}
                        </span>
                        <button onClick={() => removeItem(item.id)} disabled={isUploading} aria-label={`Remove ${item.file.name}`} className="p-1.5 rounded disabled:opacity-50 text-slate-400 hover:text-red-600"><SafeIcon icon={FiTrash2} className="w-4 h-4" /></button>
                      </div>
                    </div>
                    {item.status !== 'skipped' && item.status !== 'uploaded' && (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div><label className="block text-xs font-medium mb-1" style={{color: 'var(--mfv-text-muted)'}}>Title *</label><input type="text" value={item.title} onChange={e => updateItem(item.id, {title: e.target.value})} maxLength={config.limits.documentTitle} className="mfv-input !py-1.5" /></div>
                        <div><label className="block text-xs font-medium mb-1" style={{color: 'var(--mfv-text-muted)'}}>Profile *</label><select disabled={lockFinancialYearContext} value={item.profileId} onChange={e => updateItem(item.id, {profileId: e.target.value, financialYearId: undefined})} className="mfv-input !py-1.5"><option value="">Select Profile</option>{profiles.map(p => <option key={p.id} value={p.id}>{p.displayName}</option>)}</select></div>
                        <div><label className="block text-xs font-medium mb-1" style={{color: 'var(--mfv-text-muted)'}}>Financial Year</label><select disabled={lockFinancialYearContext} value={item.financialYearId || ''} onChange={e => updateItem(item.id, {financialYearId: e.target.value || undefined})} className="mfv-input !py-1.5"><option value="">Not year-specific</option>{itemYears.map(fy => <option key={fy.id} value={fy.id}>FY{fy.endingYear}</option>)}</select></div>
                        <div><label className="block text-xs font-medium mb-1" style={{color: 'var(--mfv-text-muted)'}}>Document Type *</label><select value={item.documentType} onChange={e => updateItem(item.id, {documentType: e.target.value as DocumentType})} className="mfv-input !py-1.5">{config.documentCategories.map(t => <option key={t} value={t}>{t}</option>)}</select></div>
                        <div className="sm:col-span-2"><label className="block text-xs font-medium mb-1" style={{color: 'var(--mfv-text-muted)'}}>Notes</label><textarea value={item.notes || ''} onChange={e => updateItem(item.id, {notes: e.target.value})} maxLength={config.limits.documentNotes} rows={2} className="mfv-input !py-1.5 resize-none" /></div>
                        <div className="sm:col-span-2"><label className="block text-xs font-medium mb-1" style={{color: 'var(--mfv-text-muted)'}}>Tags</label><input type="text" value={item.tags?.join(',') || ''} onChange={e => updateItem(item.id, {tags: e.target.value.split(',').map(t => t.trim()).filter(Boolean)})} placeholder="Comma-separated tags" className="mfv-input !py-1.5" /></div>
                        <div className="sm:col-span-2 flex items-center gap-4">
                          <label className="flex items-center text-xs" style={{color: 'var(--mfv-text-muted)'}}><input type="checkbox" checked={item.final} onChange={e => updateItem(item.id, {final: e.target.checked})} className="mr-2 rounded" />Final version</label>
                          <label className="flex items-center text-xs" style={{color: 'var(--mfv-text-muted)'}}><input type="checkbox" checked={item.sensitive} onChange={e => updateItem(item.id, {sensitive: e.target.checked})} className="mr-2 rounded" />Sensitive</label>
                        </div>
                      </div>
                    )}
                    <div className="space-y-1">
                      {error && item.status !== 'uploaded' && item.status !== 'skipped' && <p className="text-xs text-red-600">{error}</p>}
                      {item.error && <p className="text-xs text-red-600">{item.error}</p>}
                    </div>
                    {dup?.error && !dup.result.isDuplicate && (
                      <div className="bg-amber-50 border border-amber-200 rounded-lg p-2 flex items-center justify-between">
                        <p className="text-xs text-amber-800">{dup.error}</p>
                        <button onClick={() => retryDuplicateCheck(item.id)} className="text-xs font-medium text-amber-700 hover:text-amber-900"><SafeIcon icon={FiRefreshCw} className="w-3 h-3 inline mr-1" />Retry</button>
                      </div>
                    )}
                    {dup?.result.isDuplicate && dup.action === 'pending' && (
                      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                        <p className="text-xs font-medium text-amber-900 mb-2">Possible duplicate: {dup.result.existingDocumentTitle}</p>
                        <div className="flex flex-wrap gap-2">
                          <button onClick={() => setDuplicateAction(item.id, 'skip')} className="mfv-btn-secondary !px-3 !py-1.5 text-xs"><SafeIcon icon={FiSkipForward} className="w-3 h-3 mr-1" />Skip</button>
                          <button onClick={() => setDuplicateAction(item.id, 'new')} className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-white rounded" style={{backgroundColor: 'var(--mfv-primary-navy)'}}><SafeIcon icon={FiFilePlus} className="w-3 h-3 mr-1" />Upload as New</button>
                          <button onClick={() => setDuplicateAction(item.id, 'version')} className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-white rounded" style={{backgroundColor: 'var(--mfv-teal)'}}><SafeIcon icon={FiGitBranch} className="w-3 h-3 mr-1" />Add as Version</button>
                        </div>
                      </div>
                    )}
                    {item.status === 'failed' && <button onClick={retryFailed} className="text-xs font-medium text-[#0F766E]"><SafeIcon icon={FiRefreshCw} className="w-3 h-3 inline mr-1" />Retry</button>}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="px-4 lg:px-6 py-4 border-t flex items-center justify-between shrink-0 bg-white sticky bottom-0" style={{borderColor: 'var(--mfv-border)', backgroundColor: 'var(--mfv-page-bg)'}}>
          <div className="flex items-center gap-3">
            <p className="text-sm" style={{color: 'var(--mfv-text-muted)'}}>{pendingCount > 0 ? `${pendingCount} file${pendingCount !== 1 ? 's' : ''} ready` : 'All files processed'}</p>
            {failedCount > 0 && <button onClick={retryFailed} className="text-xs font-medium" style={{color: 'var(--mfv-teal)'}}><SafeIcon icon={FiRefreshCw} className="w-3 h-3 inline mr-1" />Retry Failed ({failedCount})</button>}
          </div>
          <div className="flex gap-3">
            <button onClick={handleClose} disabled={isUploading || failedCount > 0} className="mfv-btn-secondary">Cancel</button>
            <button onClick={handleUpload} disabled={isUploading || pendingCount === 0 || unresolvedDupCount > 0} className="mfv-btn-primary disabled:opacity-50 disabled:cursor-not-allowed">
              {isUploading ? <><SafeIcon icon={FiRefreshCw} className="w-4 h-4 mr-2 animate-spin" />Uploading…</> : <><SafeIcon icon={FiUpload} className="w-4 h-4 mr-2" />Upload {pendingCount} File{pendingCount !== 1 ? 's' : ''}</>}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}